import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Chip,
  Tooltip
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Close as CloseIcon,
  Description as DocumentIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  InsertDriveFile as FileIcon
} from '@mui/icons-material';
import { BACKEND_BASE_URL } from '../config';
import useResponsive from '../hooks/useResponsive';

const DOCUMENT_LABELS = {
  site_visit_report: 'Site Visit Report',
  photos_with_geolocation: 'Photos with Geo-location & Timestamp',
  approved_designs_pdf: 'Copy of Approved Designs (PDF)',
  photos: 'Multiple Photos',
  eway_bill: 'Copy of E-way Bill',
  delivery_photos: 'Photo Evidence of Delivery',
  proforma_invoice: 'Proforma Invoice',
  acknowledged_grn: 'Acknowledged GRN (Goods Receipt Note)',
  installation_photos: 'Photo Evidence of Installation',
  handover_report: 'Handover Report'
};

const getFileIcon = (filename) => {
  if (!filename) return <FileIcon />;
  
  const ext = filename.split('.').pop().toLowerCase();
  if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) {
    return <ImageIcon />;
  } else if (ext === 'pdf') {
    return <PdfIcon />;
  } else if (['doc', 'docx'].includes(ext)) {
    return <DocumentIcon />;
  }
  return <FileIcon />;
};

const getFileColor = (filename) => {
  if (!filename) return 'default';
  
  const ext = filename.split('.').pop().toLowerCase();
  if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) {
    return 'success';
  } else if (ext === 'pdf') {
    return 'error';
  } else if (['doc', 'docx'].includes(ext)) {
    return 'primary';
  }
  return 'default';
};

function DocumentUploadCard({
  title,
  documentType,
  documents = [],
  onDocumentChange,
  onDocumentDelete,
  isRequired = false,
  allowedTypes = ['jpg', 'jpeg', 'png', 'pdf', 'doc', 'docx']
}) {
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [selectedDocIndex, setSelectedDocIndex] = useState(0);
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({ open: false, index: null, fileName: '' });
  const { isSmallMobile, isMobile, getResponsiveDialogProps } = useResponsive();

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    onDocumentChange([...documents, ...files]);
  };

  const handleDeleteClick = (index) => {
    const documentToDelete = documents[index];
    const fileName = getDocumentName(documentToDelete);
    setDeleteConfirmDialog({ open: true, index, fileName });
  };

  const handleConfirmDelete = async () => {
    const { index } = deleteConfirmDialog;
    const documentToRemove = documents[index];

    // Check if this is an existing S3 file (has file_path property)
    if (documentToRemove && typeof documentToRemove === 'object' && documentToRemove.file_path) {
      // This is an existing S3 file, call the delete API
      if (onDocumentDelete) {
        try {
          await onDocumentDelete(documentToRemove.file_path);
        } catch (error) {
          console.error('Error deleting document:', error);
          setDeleteConfirmDialog({ open: false, index: null, fileName: '' });
          return; // Don't remove from UI if API call failed
        }
      }
    }

    // Remove from local state (for both new files and successfully deleted S3 files)
    const newDocuments = documents.filter((_, i) => i !== index);
    onDocumentChange(newDocuments);
    setDeleteConfirmDialog({ open: false, index: null, fileName: '' });
  };

  const handleCancelDelete = () => {
    setDeleteConfirmDialog({ open: false, index: null, fileName: '' });
  };

  const handleGalleryOpen = (index = 0) => {
    setSelectedDocIndex(index);
    setGalleryOpen(true);
  };

  const handleGalleryClose = () => {
    setGalleryOpen(false);
  };

  const getAcceptTypes = () => {
    const typeMap = {
      'jpg': 'image/jpeg',
      'jpeg': 'image/jpeg', 
      'png': 'image/png',
      'pdf': 'application/pdf',
      'doc': 'application/msword',
      'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    };
    return allowedTypes.map(type => typeMap[type]).filter(Boolean).join(',');
  };

  const isImageFile = (file) => {
    if (typeof file === 'string') {
      const ext = file.split('.').pop().toLowerCase();
      return ['jpg', 'jpeg', 'png', 'gif'].includes(ext);
    } else if (file && file.filename) {
      // S3 object with filename
      const ext = file.filename.split('.').pop().toLowerCase();
      return ['jpg', 'jpeg', 'png', 'gif'].includes(ext);
    }
    // File object
    return file.type && file.type.startsWith('image/');
  };

  const getDocumentUrl = (doc) => {
    if (typeof doc === 'string') {
      return `${BACKEND_BASE_URL}/uploads/${doc}`;
    } else if (doc && doc.presigned_url) {
      // S3 object with presigned URL
      return doc.presigned_url;
    }
    // File object
    return URL.createObjectURL(doc);
  };

  const getDocumentName = (doc) => {
    if (typeof doc === 'string') {
      return doc.split('/').pop();
    } else if (doc && doc.filename) {
      // S3 object with filename
      return doc.filename;
    }
    // File object
    return doc.name;
  };

  return (
    <>
      <Card sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        minHeight: isSmallMobile ? '200px' : '250px'
      }}>
        <CardContent sx={{
          flexGrow: 1,
          p: isSmallMobile ? 1 : 2
        }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            mb: isSmallMobile ? 1 : 2,
            flexWrap: 'wrap'
          }}>
            <Typography
              variant={isSmallMobile ? "subtitle2" : "h6"}
              sx={{
                flexGrow: 1,
                fontSize: isSmallMobile ? '0.875rem' : '1rem',
                lineHeight: 1.2,
                wordBreak: 'break-word',
                hyphens: 'auto'
              }}
              className="responsive-text"
            >
              {DOCUMENT_LABELS[documentType] || title}
            </Typography>
            {isRequired && (
              <Chip
                label="Required"
                size={isSmallMobile ? "small" : "small"}
                color="error"
                variant="outlined"
                sx={{
                  fontSize: isSmallMobile ? '0.7rem' : '0.75rem',
                  height: isSmallMobile ? '20px' : '24px'
                }}
              />
            )}
          </Box>

          <input
            type="file"
            multiple
            accept={getAcceptTypes()}
            onChange={handleFileChange}
            style={{ display: 'none' }}
            id={`file-upload-${documentType}`}
          />

          <label htmlFor={`file-upload-${documentType}`}>
            <Button
              variant="outlined"
              component="span"
              startIcon={!isSmallMobile && <UploadIcon />}
              fullWidth
              size={isSmallMobile ? "small" : "medium"}
              sx={{
                mb: isSmallMobile ? 1 : 2,
                fontSize: isSmallMobile ? '0.75rem' : '0.875rem',
                padding: isSmallMobile ? '6px 12px' : '8px 16px'
              }}
              className="touch-target"
            >
              {isSmallMobile ? 'Upload' : 'Upload Files'}
            </Button>
          </label>

          {documents.length > 0 && (
            <Box>
              <Typography
                variant={isSmallMobile ? "caption" : "body2"}
                sx={{
                  mb: 1,
                  color: 'text.secondary',
                  fontSize: isSmallMobile ? '0.7rem' : '0.875rem'
                }}
              >
                {documents.length} file{documents.length > 1 ? 's' : ''} uploaded
              </Typography>
              <Box sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: isSmallMobile ? 0.5 : 1
              }}>
                {documents.slice(0, isSmallMobile ? 2 : 3).map((doc, index) => {
                  const imageSize = isSmallMobile ? 60 : 60; // Increased mobile size to match +[number] view
                  return (
                    <Box key={index} sx={{ position: 'relative' }}>
                      {isImageFile(doc) ? (
                        <img
                          src={getDocumentUrl(doc)}
                          alt={`Document ${index + 1}`}
                          style={{
                            width: `${imageSize}px`,
                            height: `${imageSize}px`,
                            objectFit: 'cover',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                          onClick={() => handleGalleryOpen(index)}
                        />
                      ) : (
                        <Tooltip title={getDocumentName(doc)}>
                          <Box
                            sx={{
                              width: `${imageSize}px`,
                              height: `${imageSize}px`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: 'grey.100',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              border: '1px solid',
                              borderColor: 'grey.300'
                            }}
                            onClick={() => window.open(getDocumentUrl(doc), '_blank')}
                            className="touch-target"
                          >
                            {React.cloneElement(getFileIcon(getDocumentName(doc)), {
                              sx: { fontSize: isSmallMobile ? '1rem' : '1.5rem' }
                            })}
                          </Box>
                        </Tooltip>
                      )}
                      {isSmallMobile ? (
                        // Ultra-small text-based close button for mobile
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 2,
                            right: 2,
                            width: 16,
                            height: 16,
                            bgcolor: 'error.main',
                            color: 'white',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            fontSize: '10px',
                            fontWeight: 'bold',
                            lineHeight: 1,
                            '&:hover': { bgcolor: 'error.dark' },
                            zIndex: 1
                          }}
                          onClick={() => handleDeleteClick(index)}
                        >
                          ×
                        </Box>
                      ) : (
                        // Standard icon button for desktop
                        <IconButton
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: -8,
                            right: -8,
                            bgcolor: 'error.main',
                            color: 'white',
                            width: 20,
                            height: 20,
                            '&:hover': { bgcolor: 'error.dark' }
                          }}
                          onClick={() => handleDeleteClick(index)}
                        >
                          <CloseIcon sx={{ fontSize: 12 }} />
                        </IconButton>
                      )}
                    </Box>
                  );
                })}
                {documents.length > 3 && (
                  <Box
                    sx={{
                      width: '60px',
                      height: '60px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'grey.200',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                    onClick={() => handleGalleryOpen(0)}
                  >
                    <Typography variant="caption">+{documents.length - 3}</Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Gallery Dialog for Images */}
      <Dialog
        open={galleryOpen}
        onClose={handleGalleryClose}
        {...getResponsiveDialogProps()}
      >
        <DialogTitle sx={{ p: isSmallMobile ? 1 : 2 }}>
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: isSmallMobile ? 'flex-start' : 'center',
            flexDirection: isSmallMobile ? 'column' : 'row',
            gap: isSmallMobile ? 1 : 0
          }}>
            <Typography
              variant={isSmallMobile ? "subtitle1" : "h6"}
              sx={{
                fontSize: isSmallMobile ? '1rem' : '1.25rem',
                wordBreak: 'break-word'
              }}
              className="responsive-text"
            >
              {DOCUMENT_LABELS[documentType]} ({selectedDocIndex + 1} of {documents.length})
            </Typography>
            <IconButton
              onClick={handleGalleryClose}
              size={isSmallMobile ? "small" : "medium"}
            >
              <CloseIcon fontSize={isSmallMobile ? "small" : "medium"} />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{
          p: isSmallMobile ? 1 : 2,
          overflow: 'auto'
        }}>
          {documents.length > 0 && documents[selectedDocIndex] && (
            <Box sx={{ textAlign: 'center' }}>
              {isImageFile(documents[selectedDocIndex]) ? (
                <img
                  src={getDocumentUrl(documents[selectedDocIndex])}
                  alt={`Document ${selectedDocIndex + 1}`}
                  style={{
                    width: '100%',
                    height: 'auto',
                    maxHeight: isSmallMobile ? '60vh' : '70vh',
                    objectFit: 'contain'
                  }}
                />
              ) : (
                <Box sx={{
                  p: isSmallMobile ? 2 : 4,
                  textAlign: 'center'
                }}>
                  <Box sx={{
                    mb: 2,
                    fontSize: isSmallMobile ? '3rem' : '4rem',
                    color: getFileColor(getDocumentName(documents[selectedDocIndex]))
                  }}>
                    {getFileIcon(getDocumentName(documents[selectedDocIndex]))}
                  </Box>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    {getDocumentName(documents[selectedDocIndex])}
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => window.open(getDocumentUrl(documents[selectedDocIndex]), '_blank')}
                  >
                    Open Document
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setSelectedDocIndex(Math.max(0, selectedDocIndex - 1))}
            disabled={selectedDocIndex === 0}
          >
            Previous
          </Button>
          <Button
            onClick={() => setSelectedDocIndex(Math.min(documents.length - 1, selectedDocIndex + 1))}
            disabled={selectedDocIndex === documents.length - 1}
          >
            Next
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmDialog.open}
        onClose={handleCancelDelete}
        maxWidth="sm"
        fullWidth
        {...getResponsiveDialogProps()}
      >
        <DialogTitle>
          <Typography variant="h6" sx={{ color: 'error.main', fontWeight: 'bold' }}>
            ⚠️ Confirm Delete
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to permanently delete this file?
          </Typography>
          <Typography variant="body2" sx={{
            fontWeight: 'bold',
            color: 'text.primary',
            bgcolor: 'grey.100',
            p: 1,
            borderRadius: 1,
            wordBreak: 'break-word'
          }}>
            📄 {deleteConfirmDialog.fileName}
          </Typography>
          <Typography variant="body2" sx={{ mt: 2, color: 'error.main', fontStyle: 'italic' }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{
          p: isSmallMobile ? 2 : 3,
          gap: isSmallMobile ? 1 : 0,
          flexDirection: isSmallMobile ? 'column' : 'row'
        }}>
          <Button
            onClick={handleCancelDelete}
            color="inherit"
            variant="outlined"
            fullWidth={isSmallMobile}
            sx={{ minWidth: isSmallMobile ? '100%' : '100px' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            fullWidth={isSmallMobile}
            sx={{ minWidth: isSmallMobile ? '100%' : '100px' }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default DocumentUploadCard;
